// What the Publish room lists and checks before building (SuiteCompile/Publish.swift): `PublishFormat` (each built-in
// format as its presets, then "More formats" and "Your formats") and `PublishReadiness`.
import { blockPlainText, editionLabel, ISBN, isbnOf, isStageFormat, MergeTags, trimCFWhitespace, wordCount, type ProjectKind,
  type ScriptFormat } from '@writing-suite/core';
import { stageScript } from './screenplay/stage.ts';
import {
  formatted, heading, isDocument, isPenName, contactName, withholdsOwnName, isPost, publicationEdition, roundedWords, titlePageContent, wordCountPhrase,
} from './manuscript.ts';
import type { FormatSetting } from './formatSettings.ts';
import { documentFragment, keepsMarks } from './pipeline.ts';
import type { CompileSource } from './source.ts';
import type { AuthorInfo, BookMatter, CompiledManuscript, CompilePreset, OutputFormat } from './types.ts';

export type PublishFormatKind = 'manuscript' | 'ebook' | 'print' | 'audiobook' | 'submission' | 'screenplay' | 'post' | 'preset';

export interface PublishFormat {
  id: string;
  kind: PublishFormatKind;
  title: string;
  subtitle: string;
  lede: string;
  /** The presets it can be built with; the first is the usual one. A submission pack builds them all. */
  variants: { presetID: string; label: string }[];
  /** The build button's label. */
  action: string;
}

export const presetIDsOf = (f: PublishFormat) => f.variants.map((v) => v.presetID);

/** The documents a submission pack holds, in the order they're listed. */
export const SUBMISSION_PRESETS = ['synopsis', 'query-letter', 'pitch', 'logline'];

const v = (presetID: string, label: string) => ({ presetID, label });

/** `PublishFormat.builtIns(kind:format:)`: `format`, the script's (absent: the kind's usual one), decides a play's
 *  versions and whether a screenplay is a sitcom. A project type is also a kind ('manuscript', 'screenplay', 'journal'). */
/** `CompilePreset.isPoetry`: a poetry collection's preset, whose map gives a Poem section type a role of its own. */
export const isPoetryPreset = (p: CompilePreset): boolean => p.map.rules.some((r) => r.when.sectionType === 'Poem');

/** `poetry`: a poetry collection (the template), whose own formats come first; the novel's go under More formats. */
export function builtInFormats(kind: ProjectKind, format?: ScriptFormat, musical = false, poetry = false): PublishFormat[] {
  const stage = format ?? (kind === 'stage-play' ? 'stage-us' : kind === 'radio-play' ? 'radio' : 'screen');
  if (kind === 'manuscript' && poetry) {
    return [
      { id: 'print', kind: 'print', title: 'Poetry collection', subtitle: 'PDF · paperback, 5.5 × 8.5 in',
        lede: "Each poem on its own page under its title, set ragged with its lines' steps held and a wrapped line hanging under its first word. Short poems can run several to a page.",
        variants: [v('poetry-print', 'KDP'), v('poetry-print-run', 'Short poems, several a page'), v('poetry-print-ingramspark', 'IngramSpark (PDF/X-1a)')],
        action: 'Build print PDF' },
      { id: 'ebook', kind: 'ebook', title: 'Poetry ebook', subtitle: 'EPUB · Apple Books, Kobo, Kindle',
        lede: "Each poem a chapter the reader's contents list, its lines stepped as written and hanging where a phone wraps them.",
        variants: [v('poetry-ebook', 'EPUB')], action: 'Build ebook' },
      { id: 'manuscript', kind: 'manuscript', title: 'Poetry manuscript', subtitle: 'Word · for journals and competitions',
        lede: 'Each poem on a new page under its title, the verse single-spaced in named Verse styles a journal can restyle.',
        variants: [v('poetry-manuscript', 'Word')], action: 'Prepare manuscript' },
      { id: 'submission', kind: 'submission', title: 'Submission pack', subtitle: 'Synopsis, query letter, pitch',
        lede: 'The short documents that travel with the manuscript, from Publishing ▸ Submission.',
        variants: [v('synopsis', 'Synopsis'), v('query-letter', 'Query letter'), v('pitch', 'Pitch'), v('logline', 'Logline')],
        action: 'Prepare pack' },
    ];
  }
  if (kind === 'journal') {
    return [
      { id: 'post', kind: 'post', title: 'Post', subtitle: 'One post · to share or paste',
        lede: 'The post you choose, with the journal\'s opening and sign-off, to paste into Substack or send to whoever publishes it. Merge tags such as [FIRSTNAME] stay as typed.',
        variants: [v('post-html', 'Web page (HTML)'), v('post-docx', 'Word'), v('post-markdown', 'Markdown'), v('post-text', 'Plain text')],
        action: 'Prepare post' },
      { id: 'journal-ebook', kind: 'ebook', title: 'Journal as ebook', subtitle: 'EPUB · every post, oldest first',
        lede: 'The journal as a book: each post a chapter under its own title, with your front and back matter.',
        variants: [v('journal-ebook', 'EPUB')], action: 'Build ebook' },
      { id: 'journal-print', kind: 'print', title: 'Journal as paperback', subtitle: 'PDF · every post, oldest first',
        lede: 'A print-ready paperback interior of every post, with running heads and your front and back matter.',
        variants: [v('journal-print', 'KDP')], action: 'Build print PDF' },
    ];
  }
  const pack: PublishFormat = {
    id: 'submission', kind: 'submission', title: 'Submission pack', subtitle: 'Synopsis, query letter, pitch',
    lede: 'The short documents that travel with the manuscript, from Publishing ▸ Submission.',
    variants: [v('synopsis', 'Synopsis'), v('query-letter', 'Query letter'), v('pitch', 'Pitch'), v('logline', 'Logline')],
    action: 'Prepare pack',
  };
  if (kind === 'radio-play') {
    // What radio producers and drama competitions ask for: a page of synopsis, the cast and the script.
    const radioPack: PublishFormat = {
      id: 'submission', kind: 'submission', title: 'Submission pack', subtitle: 'Synopsis, cover letter, cast list, script',
      lede: 'What producers and drama competitions ask for alongside the play: a one-page synopsis, a covering letter, the cast list and the script, from Publishing ▸ Submission and the play itself.',
      variants: [v('synopsis', 'Synopsis'), v('query-letter', 'Cover letter'), v('character-breakdown', 'Cast list'), v('radio', 'Script'),
        v('pitch', 'Pitch'), v('logline', 'Logline')],
      action: 'Prepare pack',
    };
    return [
      { id: 'radio', kind: 'screenplay', title: 'Radio play', subtitle: 'PDF · BBC scene style',
        lede: 'Arial 12 on A4, about a minute a page: scenes numbered, each name at the margin with its speech beside it, technical directions and sound cues in capitals and underlined, with a title page and a cast list.',
        variants: [v('radio', 'Submission'), v('radio-studio', 'Studio (numbered speeches)'), v('radio-docx', 'Word'), v('radio-fountain', 'Fountain')],
        action: 'Build radio play' },
      radioPack,
    ];
  }
  if (kind === 'screenplay' && stage === 'multicam') {
    return [
      { id: 'episode', kind: 'screenplay', title: 'Episode', subtitle: 'PDF · multi-camera format',
        lede: 'Courier 12, about thirty seconds a page: each act and scene on a new page under its heading and letter, action in capitals with entrances and exits underlined, dialogue double-spaced.',
        variants: [v('sitcom', 'US Letter'), v('sitcom-docx', 'Word'), v('sitcom-fountain', 'Fountain')], action: 'Build episode' },
      pack,
    ];
  }
  if (kind === 'stage-play') {
    // What theatres and competitions ask for: the documents, the character breakdown and a ten-page sample.
    const playPack: PublishFormat = {
      id: 'submission', kind: 'submission', title: 'Submission pack', subtitle: 'Synopsis, cover letter, characters, sample',
      lede: 'What theatres and competitions ask for alongside the play: the documents in Publishing ▸ Submission that are written, the character breakdown and the first ten pages.',
      variants: [v('synopsis', 'Synopsis'), v('query-letter', 'Cover letter'), v('character-breakdown', 'Characters'),
        // A musical's numbers travel with it.
        ...(musical ? [v('musical-numbers', 'Musical numbers')] : []),
        v(stage === 'stage-uk' ? 'stage-play-uk-sample' : 'stage-play-sample', 'Ten-page sample'), v('development-history', 'Development history'),
        v('pitch', 'Pitch'), v('logline', 'Logline')],
      action: 'Prepare pack',
    };
    return [
      stage === 'stage-uk'
        ? { id: 'play', kind: 'screenplay', title: 'Play', subtitle: 'PDF · UK manuscript format',
          lede: 'Times New Roman 12 on A4: each name in bold at the margin with its speech beside it, directions in italics, each act and scene on a new page, with a title page and a cast of characters.',
          variants: [v('stage-play-uk', 'A4'), v('stage-play-uk-docx', 'Word'), v('stage-play-fountain', 'Fountain')], action: 'Build play' }
        : { id: 'play', kind: 'screenplay', title: 'Play', subtitle: 'PDF · US manuscript format',
          lede: 'Courier 12, a page a minute: names over their speeches, directions indented in brackets, each act and scene on a new page, with a title page and a cast of characters.',
          variants: [v('stage-play', 'US Letter'), v('stage-play-a4', 'A4'), v('stage-play-docx', 'Word'), v('stage-play-fountain', 'Fountain')],
          action: 'Build play' },
      playPack,
    ];
  }
  if (kind === 'screenplay') {
    return [
      { id: 'screenplay', kind: 'screenplay', title: 'Screenplay', subtitle: 'PDF · industry standard',
        lede: "Courier 12, a page a minute: scene headings kept with what follows, (MORE) and (CONT'D) where speeches break.",
        variants: [v('screenplay', 'US Letter'), v('screenplay-a4', 'A4'), v('shooting-script', 'Shooting script'),
          v('screenplay-fdx', 'Final Draft (.fdx)'), v('screenplay-fountain', 'Fountain')],
        action: 'Build screenplay' },
      pack,
    ];
  }
  return [
    { id: 'manuscript', kind: 'manuscript', title: 'Manuscript', subtitle: 'Shunn standard · for agents',
      lede: 'Double-spaced, with your name and the page on every page. What an agent or editor expects.',
      variants: [v('shunn-docx', 'Word'), v('shunn-pdf', 'PDF'), v('blind-submission', 'Blind (no name)')], action: 'Prepare manuscript' },
    { id: 'ebook', kind: 'ebook', title: 'Ebook', subtitle: 'EPUB · Apple Books, Kobo, Kindle',
      lede: 'Reflowable, with a linked contents page, your front and back matter, and accessibility details.',
      variants: [v('ebook', 'EPUB')], action: 'Build ebook' },
    { id: 'print', kind: 'print', title: 'Print interior', subtitle: 'PDF · paperback, KDP or IngramSpark',
      lede: "Mirrored margins, running heads, chapters opening on the right. Lost scene breaks and short spreads are fixed as it's set.",
      variants: [v('print-interior', 'KDP'), v('print-interior-ingramspark', 'IngramSpark (PDF/X-1a)')], action: 'Build print PDF' },
    { id: 'audiobook', kind: 'audiobook', title: 'Audiobook', subtitle: 'M4B · chapters, ACX levels',
      lede: 'The book read aloud, a chapter a track, mastered to the levels audiobook stores ask for.',
      variants: [v('audiobook', 'M4B'), v('narration-script', 'Script (text)')], action: 'Record audiobook' },
    pack,
  ];
}

/** `PublishFormat.describe`. */
export function describeFormat(format: OutputFormat): string {
  switch (format) {
    case 'docx': return 'Word (.docx)';
    case 'pdf': return 'PDF';
    case 'epub': return 'EPUB';
    case 'markdown': return 'Markdown';
    case 'text': return 'Plain text';
    case 'audiobook': return 'M4B audiobook';
    case 'fdx': return 'Final Draft (.fdx)';
    case 'fountain': return 'Fountain';
    case 'html': return 'HTML';
  }
}

const byName = (a: CompilePreset, b: CompilePreset) => (a.name < b.name ? -1 : a.name > b.name ? 1 : 0);

/** `PublishFormat.others`: the presets no built-in format covers (`more`) and the project's own (`yours`). */
export function otherFormats(presets: CompilePreset[], projectPresetIDs: Iterable<string>, kind: ProjectKind,
  scriptFormat?: ScriptFormat, musical = false, poetry = false): { more: PublishFormat[]; yours: PublishFormat[] } {
  const own = new Set(projectPresetIDs);
  const covered = new Set(builtInFormats(kind, scriptFormat, musical, poetry).flatMap(presetIDsOf));
  const format = (p: CompilePreset): PublishFormat => ({
    id: 'preset:' + p.id, kind: 'preset', title: p.name, subtitle: describeFormat(p.format), lede: p.description,
    variants: [v(p.id, describeFormat(p.format))], action: 'Build',
  });
  // A built-in's adjusted version (`FormatAdjustment`) stands in its place, never under Your formats.
  const byID = new Map(presets.map((p) => [p.id, p]));
  const adjusted = (id: string) => id.startsWith('adjusted-');
  const inPlace = (p: CompilePreset) => (own.has('adjusted-' + p.id) ? byID.get('adjusted-' + p.id) ?? p : p);
  return {
    more: presets.filter((p) => !own.has(p.id) && !covered.has(p.id) && !adjusted(p.id)).sort(byName)
      .map((p) => ({ ...format(inPlace(p)), id: 'preset:' + p.id })),
    yours: presets.filter((p) => own.has(p.id) && !adjusted(p.id)).sort(byName).map(format),
  };
}

// MARK: PublishReadiness

export interface ReadinessItem {
  id: string;
  status: 'ready' | 'attention' | 'note';
  message: string;
  fragmentID?: string;
  /** A format setting that answers the item (Publish offers it as one action: "Keep Them" for `keepHighlights`). */
  setting?: FormatSetting;
}

const item = (status: ReadinessItem['status'], id: string, message: string, fragmentID?: string | null, setting?: FormatSetting): ReadinessItem =>
  ({ id, status, message, ...(fragmentID != null ? { fragmentID } : {}), ...(setting !== undefined ? { setting } : {}) });

/** What the last print build found, kept by the app between builds. */
export interface LastPrint { pages: number; ornamentedBreaks: number; loosenedParagraphs: number; presetID?: string; size?: string }

export interface PackDocument { kind: BookMatter; fragmentID: string | null; words: number }

export interface ReadinessOptions {
  /** The characters and places, for the audiobook. */
  names?: string[];
  lastPrint?: LastPrint | null;
  /** A screenplay's page count (W5b's `ScreenplayTypst.pageCount`). */
  screenplayPages?: number;
}

/** `PublishReadiness.check`: cheap checks on a prepared (not rendered) manuscript. */
export function readiness(m: CompiledManuscript, author: AuthorInfo | null, o: ReadinessOptions = {}): ReadinessItem[] {
  if (isDocument(m)) return [];
  const preset = m.preset;
  const items: ReadinessItem[] = [];
  if (m.wordCount === 0) return [item('attention', 'empty', isPost(m) ? 'This post has no text yet.' : "There's nothing to build yet: the manuscript has no text.")];
  // A post has no chapters, title page or edition to check.
  if (preset.journal === true && isPost(m)) return journalChecks(m);
  if (isPoetryPreset(preset)) {
    // A poem's paragraphs set as prose take the body's indent and justification: things to look at, by poem.
    const prose = m.sections.filter((s) => s.role === 'chapter' && s.blocks.some((b) => b.kind.type === 'paragraph' && b.alignment === null));
    if (prose.length) {
      const names = prose.slice(0, 3).map((s) => `“${s.title}”`).join(', ');
      items.push(item('note', 'poem.prose', `${plural(prose.length, 'poem')} ${prose.length === 1 ? 'has' : 'have'} paragraphs set as prose rather than verse: ${names}${prose.length > 3 ? '…' : ''}. Choose Verse for them in Write.`,
        prose[0].fragmentID));
    }
  }
  const tp = preset.render.titlePage;
  const titled = titlePageContent(m) !== null;
  if (preset.render.screenplay) {
    const pages = o.screenplayPages ?? 0;
    const script = preset.render.screenplay;
    const play = isStageFormat(script.format ?? 'screen');
    const p = `${formatted(pages)} ${pages === 1 ? 'page' : 'pages'}`;
    const format = script.format ?? 'screen';
    if (play && m.musical && !script.sample) {
      // A musical's length is its book and its songs: songs run longer than they print.
      const songs = stageScript(m).songs.length;
      const minutes = runningMinutes(pages, format, songs);
      items.push(item('ready', 'script.pages', `${plural(pages, 'page')} of book and ${plural(songs, 'song')}: roughly ${runningPhrase(minutes)}, at a page a minute and three and a half minutes a song`));
      if (songs === 0) {
        items.push(item('note', 'songs.none', 'No songs yet: a line starting “Song:” names one where it begins, and numbers it.'));
      } else {
        const numbers = m.sections.find((s) => s.matter === 'musical-numbers');
        if (numbers) {
          items.push(numbers.blocks.length === 0
            ? item('ready', 'songs', `Musical Numbers page made from the ${plural(songs, 'song')}; write your own in Publishing ▸ Front Matter to set it yourself`)
            : item('ready', 'songs', 'Your Musical Numbers page, from Publishing ▸ Front Matter'));
        }
      }
    } else if (format === 'multicam') {
      items.push(item('ready', 'script.pages', `About ${plural(pages, 'page')}: roughly ${runningPhrase(runningMinutes(pages, format))}, at about thirty seconds a page`));
      if (pages < 38 || pages > 55) items.push(item('note', 'script.halfHour', `A half-hour multi-camera episode usually runs 38 to 55 pages; this runs ${formatted(pages)}.`));
    } else if (format === 'radio') {
      items.push(item('ready', 'script.pages', `About ${plural(pages, 'page')}: roughly ${runningPhrase(pages)} on air, at a page a minute`));
      const note = radioSlotNote(pages);
      if (note !== null) items.push(item('note', 'script.slot', note));
    } else if (play && script.sample) {
      items.push(item('ready', 'script.pages', script.sample.from !== undefined
        ? `A sample from “${script.sample.from}”: ${p}, ending with the last speech or direction that fits`
        : `The first ${p}, ending with the last speech or direction that fits`));
    } else if (play) {
      // Running time: a page a minute, near enough.
      items.push(item('ready', 'script.pages', `About ${p}: roughly ${formatted(pages)} ${pages === 1 ? 'minute' : 'minutes'} on stage, at a page a minute`));
      if (pages >= 11 && pages <= 15) {
        items.push(item('note', 'script.tenMinute', `For a ten-minute play festival: most take 10 pages at most, and this runs ${formatted(pages)}.`));
      }
    } else {
      items.push(item('ready', 'script.pages', `About ${p}, a page a minute`));
    }
    // A play's (or radio play's) Cast of Characters: the writer's, or made from who speaks.
    const page = play || format === 'radio' ? m.sections.find((s) => s.matter === 'cast-list') : undefined;
    if (page) {
      const cast = stageScript(m).cast.length;
      if (page.blocks.length === 0) {
        items.push(cast === 0
          ? item('attention', 'cast.empty', "Nobody speaks yet, so there's no Cast of Characters to make. Write one in Publishing ▸ Front Matter.")
          : item('ready', 'cast', `Cast of Characters made from the ${cast === 1 ? 'one character' : `${cast} characters`} who speak; write your own in Publishing ▸ Front Matter to describe them`));
      } else {
        items.push(item('ready', 'cast', 'Your Cast of Characters, from Publishing ▸ Front Matter'));
      }
    }
  } else if (tp.include && tp.wordCount && titled) {
    items.push(item('ready', 'words', `Word count rounded to ${wordCountPhrase(m)}`));
  }
  if (preset.blind) {
    items.push(item('ready', 'blind', "Your name is left out, and the file is checked for it when it's built"));
  } else if (tp.include && tp.contact && (titled || preset.render.screenplay)) {
    if (!author || trimCFWhitespace(author.name) === '') {
      items.push(item('attention', 'contact.name', 'Add your name and contact details in Settings ▸ Author: the title page needs them.'));
    } else if (!author.contact.length) {
      items.push(item('attention', 'contact.details', withholdsOwnName(author)
        ? `No contact details on the title page. Add an address or email for ${author.name} in Settings ▸ Author ▸ Pen names.`
        : 'No contact details on the title page. Add an address or email in Settings ▸ Author.'));
    } else {
      items.push(item('ready', 'contact', withholdsOwnName(author)
        ? `Contact details for ${author.name} from Settings ▸ Author ▸ Pen names` : 'Contact details from Settings ▸ Author'));
    }
  }
  if (!preset.blind && author && (isPenName(author) || withholdsOwnName(author))) {
    items.push(item('ready', 'pen-name', !(tp.include && tp.contact) ? `Published as ${author.name}`
      : withholdsOwnName(author) ? `Published as ${author.name}, with its own contact details: your own name is left out`
      : `Published as ${author.name}; the title page gives ${contactName(author)} to write to`));
  }
  if (!preset.render.screenplay && preset.format !== 'audiobook') items.push(...chapterChecks(m));
  // The writer's highlights and text colours: a Word file can keep them for an editor; nothing else shows them.
  if (m.hasMarks === true && preset.format === 'docx') {
    items.push(keepsMarks(preset)
      ? item('ready', 'marks', 'Your highlights and text colours are kept in the file, for whoever reads it next')
      : item('note', 'marks', "Your highlights and text colours won't be in this file. Keep them if they're notes for your editor.", null, 'keepHighlights'));
  }
  const edition = publicationEdition(preset);
  if (edition !== null && edition !== 'audiobook' && preset.select.frontMatter && !preset.blind) {
    const isbn = isbnOf(m.publication, edition);
    const label = editionLabel(edition).toLowerCase();
    const problem = ISBN.problem(isbn);
    if (trimCFWhitespace(isbn) === '') items.push(item('note', 'isbn.missing', `No ${label} ISBN. Add one in Publication details, or leave it to your printer or store.`));
    else if (problem) items.push(item('attention', 'isbn.invalid', `The ${label} ISBN doesn't check out. ${problem}`));
    else items.push(item('ready', 'isbn', `${editionLabel(edition)} ISBN checks out`));
  }
  if (preset.format === 'epub') items.push(item('ready', 'contents', 'Contents page generated and linked'));
  if (preset.render.print) {
    const last = o.lastPrint;
    if (last) {
      if (last.ornamentedBreaks > 0) {
        items.push(item('note', 'print.breaks', `Last build: ${last.ornamentedBreaks} scene ${last.ornamentedBreaks === 1 ? 'break fell' : 'breaks fell'} at a page edge and got an ornament`));
      } else {
        items.push(item('ready', 'print.breaks', 'Last build: no scene break lost at a page edge'));
      }
      if (last.loosenedParagraphs > 0) {
        items.push(item('note', 'print.spreads', `Last build: ${last.loosenedParagraphs} ${last.loosenedParagraphs === 1 ? 'paragraph' : 'paragraphs'} set a hair looser to even up facing pages`));
      }
    } else {
      items.push(item('note', 'print.breaks', "Scene breaks at a page edge and uneven facing pages are fixed as it's set"));
    }
  }
  if (preset.journal === true) items.push(...journalChecks(m));
  if (preset.format === 'audiobook' || preset.render.audio) {
    items.push(...pronunciationChecks(m, o.names ?? []));
    if (preset.format === 'audiobook') items.push(item('ready', 'audio.levels', 'Mastered to audiobook levels (−20 dB, peaks under −3 dB)'));
  }
  return items;
}

/** A journal post (or the journal): its number, numbers two posts share, merge tags that look broken. */
export function journalChecks(m: CompiledManuscript): ReadinessItem[] {
  const items: ReadinessItem[] = [];
  const first = m.sections.find((s) => s.fragmentID !== null);
  if (m.preset.select.postMatter === true && isPost(m) && first) {
    const own = first.fragmentID !== null && (m.ownPostMatter ?? []).includes(first.fragmentID);
    if (own) items.push(item('ready', 'post.matter.own', 'Opening and sign-off as written in the post, placeholders filled in', first.fragmentID));
    const number = first.fragmentID !== null ? m.provisionalNumbers[first.fragmentID] : undefined;
    if (number !== undefined) {
      items.push(item('note', 'post.number', `Not scheduled or published yet, so the opening shows No. ${number}, the next number. It's fixed when the post goes out.`, first.fragmentID));
    } else if (!own) {
      items.push(item('ready', 'post.number', 'Opening and sign-off from Publishing ▸ Opening & Sign-off'));
    }
  } else if (m.preset.select.postMatter === true) {
    items.push(item('ready', 'post.matter', 'Every post opens and closes with Publishing ▸ Opening & Sign-off'));
  }
  const { shared, missing } = m.postNumberProblems;
  if (shared.length) {
    const list = shared.slice(0, 5).map((n) => `No. ${n}`).join(', ');
    items.push(item('attention', 'post.shared', `Two posts share ${shared.length === 1 ? 'a number' : 'numbers'}: ${list}. Change one in its Post number field.`));
  }
  if (missing.length && missing.length <= 20) {
    items.push(item('note', 'post.gaps', `No post has ${missing.slice(0, 5).map((n) => `No. ${n}`).join(', ')}${missing.length > 5 ? ` and ${missing.length - 5} more` : ''}.`));
  }
  const text = m.sections.flatMap((s) => s.blocks).map(blockPlainText).join('\n');
  const tags = MergeTags.matches(text, m.mergeTags);
  if (tags.length) {
    const names = [...new Set(tags.map((t) => t.name))].sort().slice(0, 4).join(', ');
    items.push(item('ready', 'merge.tags', `Merge tags kept as typed: ${names}`));
  }
  const broken = MergeTags.broken(text, m.mergeTags);
  if (broken.length) {
    items.push(item('attention', 'merge.broken', `This looks like a merge tag gone wrong: ${broken.slice(0, 3).map((b) => `“${b}”`).join(', ')}. Tags are in capitals between square brackets, like [FIRSTNAME].`));
  }
  return items;
}

/** Chapters with no title (numbered only), and titles that repeat their own number. */
export function chapterChecks(m: CompiledManuscript): ReadinessItem[] {
  const template = m.preset.render.chapterHeading;
  const chapters = m.sections.filter((s) => s.role === 'chapter');
  if (!chapters.length) return [];
  const items: ReadinessItem[] = [];
  if (template.some((t) => t.includes('{title}'))) {
    const untitled = chapters.filter((c) => trimCFWhitespace(c.title) === '');
    if (untitled.length) {
      const one = untitled.length === 1;
      const consequence = heading(template, untitled[0]).length
        ? `so ${one ? "it's" : "they're"} numbered only`
        : m.preset.format === 'epub'
          ? `and chapter headings are the title alone, so the ebook heads ${one ? 'it' : 'them'} “Chapter” and a number`
          : `and chapter headings are the title alone, so ${one ? 'it has' : 'they have'} no heading`;
      items.push(item('attention', 'chapters.untitled', `${count(untitled.length, 'chapter')} ${one ? 'has' : 'have'} no title, ${consequence}.`, untitled[0].fragmentID));
    }
    const doubled = chapters.filter((c) => {
      const lines = heading(template, c).map((l) => l.toLowerCase());
      return new Set(lines).size < lines.length;
    });
    if (doubled.length) {
      const example = heading(template, doubled[0]).join(' / ');
      const one = doubled.length === 1;
      items.push(item('attention', 'chapters.doubled',
        `${count(doubled.length, 'chapter')} ${one ? 'is' : 'are'} titled like ${one ? 'its' : 'their'} number, so the heading says it twice: “${example}”.`, doubled[0].fragmentID));
    }
  }
  if (!items.length) items.push(item('ready', 'chapters', `${count(chapters.length, 'chapter')}, every one titled`));
  return items;
}

const reEscape = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/** Characters and places the book names that have no pronunciation yet. */
export function pronunciationChecks(m: CompiledManuscript, names: string[]): ReadinessItem[] {
  const keys = Object.keys(m.audiobook.pronunciations);
  const exact = new Set(keys), caseless = new Set(keys.map((k) => k.toLowerCase()));
  const text = m.sections.map((s) => s.source ?? s.blocks.map(blockPlainText).join('\n')).join('\n');
  const missing: string[] = [];
  for (const name of [...new Set(names.map(trimCFWhitespace))].sort()) {
    if (name === '' || exact.has(name) || caseless.has(name.toLowerCase())) continue;
    if (new RegExp(`(?<![\\p{L}\\p{N}])${reEscape(name)}(?![\\p{L}\\p{N}])`, 'u').test(text)) missing.push(name);
  }
  if (!missing.length) return names.length ? [item('ready', 'audio.names', 'Every character and place has a pronunciation')] : [];
  const shown = missing.slice(0, 4).join(', ') + (missing.length > 4 ? ` and ${missing.length - 4} more` : '');
  return [item('attention', 'audio.names', `${count(missing.length, 'name')} without a pronunciation: ${shown}. Check how the voice says ${missing.length === 1 ? 'it' : 'them'}.`)];
}

/** `submissionPack`: the Submission documents and what's worth knowing before they're sent. */
export function submissionPack(source: CompileSource, manuscriptWords: number, kind: ProjectKind = 'manuscript',
  pages: number | null = null, musical = false): { items: ReadinessItem[]; documents: PackDocument[] } {
  if (kind === 'stage-play') return playPack(source, pages, musical);
  if (kind === 'radio-play') return radioPack(source, pages);
  const items: ReadinessItem[] = [];
  const documents: PackDocument[] = [];
  for (const kind of ['synopsis', 'query-letter', 'pitch', 'logline'] as const) {
    const fragment = documentFragment(kind, source);
    const text = fragment?.text ?? '';
    const words = wordCount(text);
    const id = fragment?.id ?? null;
    documents.push({ kind, fragmentID: id, words });
    switch (kind) {
      case 'synopsis':
        if (words === 0) items.push(item('attention', 'synopsis.empty', 'The synopsis is empty. Write it in Publishing ▸ Submission ▸ Synopsis.', id));
        else if (words <= 1000) items.push(item('ready', 'synopsis', `Synopsis is ${formatted(words)} words, about ${words <= 550 ? 'a page' : 'two pages'} single-spaced`, id));
        else items.push(item('attention', 'synopsis.long', `Synopsis is ${formatted(words)} words. Agents usually ask for one or two pages (500 to 1,000 words).`, id));
        break;
      case 'query-letter':
        if (words === 0) {
          items.push(item('attention', 'query.empty', 'The query letter is empty. Write it in Publishing ▸ Submission ▸ Query Letter.', id));
          break;
        }
        if (words > 450) items.push(item('attention', 'query.long', `Query letter is ${formatted(words)} words; most agents expect 250 to 400.`, id));
        items.push(queryWordCount(text, manuscriptWords, id));
        break;
      case 'pitch':
        if (words === 0) items.push(item('note', 'pitch.empty', 'No pitch yet; the pack leaves it out.', id));
        break;
      case 'logline':
        if (words === 0) items.push(item('note', 'logline.empty', 'No logline yet; the pack leaves it out.', id));
        else if (words > 60) items.push(item('attention', 'logline.long', `Logline is ${formatted(words)} words; one sentence of 25 to 40 reads best.`, id));
        break;
    }
  }
  return { items, documents };
}

/** Whether the length a query letter gives matches the book's. */
export function queryWordCount(text: string, manuscriptWords: number, fragmentID: string | null): ReadinessItem {
  if (text.includes('{words}')) return item('ready', 'query.words', `The book's length is filled in as ${roundedWords(manuscriptWords)} words`, fragmentID);
  const mentioned = mentionedWords(text);
  if (mentioned === null) return item('note', 'query.words', "The query letter doesn't give the book's length. Type {words} where it should go.", fragmentID);
  const tolerance = Math.max(1000, manuscriptWords * 0.03);
  if (Math.abs(mentioned - manuscriptWords) > tolerance) {
    return item('attention', 'query.words', `Query letter says ${formatted(mentioned)} words; the book is about ${roundedWords(manuscriptWords)}.`, fragmentID);
  }
  return item('ready', 'query.words', "Query letter's word count matches the book", fragmentID);
}

/** "90,000 words", "90,000-word", "90000 words" or "90k words" → 90000. */
export function mentionedWords(text: string): number | null {
  const a = /(\d{1,3}(?:,\d{3})+|\d{4,7})[\s -]*words?\b/i.exec(text);
  if (a) return Number(a[1].replace(/\D/g, ''));
  const b = /\b(\d{2,3})\s*k[\s-]*words?\b/i.exec(text);
  return b ? Number(b[1]) * 1000 : null;
}

export function count(n: number, noun: string): string {
  const words = ['No', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine'];
  return (n < words.length ? words[n] : formatted(n)) + ' ' + noun + (n === 1 ? '' : 's');
}

/** `PublishReadiness.playPack`: theatres ask for a page of synopsis, a cover letter, the characters, a sample and where the
 *  play has been; nobody asks its word count. */
export function playPack(source: CompileSource, pages: number | null, musical = false): { items: ReadinessItem[]; documents: PackDocument[] } {
  const items: ReadinessItem[] = [];
  const documents: PackDocument[] = [];
  const kinds: BookMatter[] = ['synopsis', 'query-letter', 'cast-list', ...(musical ? ['musical-numbers' as const] : []), 'development-history', 'pitch', 'logline'];
  for (const kind of kinds) {
    const fragment = documentFragment(kind, source);
    const words = wordCount(fragment?.text ?? '');
    const id = fragment?.id ?? null;
    documents.push({ kind, fragmentID: id, words });
    switch (kind) {
      case 'synopsis':
        if (words === 0) items.push(item('attention', 'synopsis.empty', 'The synopsis is empty. Write it in Publishing ▸ Submission ▸ Synopsis.', id));
        else if (words <= 600) items.push(item('ready', 'synopsis', `Synopsis is ${formatted(words)} words, about a page single-spaced`, id));
        else items.push(item('attention', 'synopsis.long', `Synopsis is ${formatted(words)} words. Theatres usually ask for a page (about 500 words).`, id));
        break;
      case 'query-letter':
        if (words === 0) items.push(item('attention', 'query.empty', 'The cover letter is empty. Write it in Publishing ▸ Submission ▸ Query Letter.', id));
        else if (words > 450) items.push(item('attention', 'query.long', `Cover letter is ${formatted(words)} words; a page (250 to 400) is usual.`, id));
        break;
      case 'cast-list':
        items.push(words === 0
          ? item('ready', 'cast', 'Characters are made from who speaks; write the Cast of Characters in Publishing ▸ Front Matter to describe them', id)
          : item('ready', 'cast', 'Characters from your Cast of Characters', id));
        break;
      case 'musical-numbers':
        items.push(words === 0
          ? item('ready', 'songs', 'Musical numbers are made from the songs; write the Musical Numbers page in Publishing ▸ Front Matter to set them yourself', id)
          : item('ready', 'songs', 'Musical numbers from your Musical Numbers page', id));
        break;
      case 'development-history':
        if (words === 0) items.push(item('note', 'history.empty', 'No development history yet; the pack leaves it out. Theatres and competitions ask for readings, workshops and productions.', id));
        break;
      case 'pitch':
        if (words === 0) items.push(item('note', 'pitch.empty', 'No pitch yet; the pack leaves it out.', id));
        break;
      case 'logline':
        if (words === 0) items.push(item('note', 'logline.empty', 'No logline yet; the pack leaves it out.', id));
        else if (words > 60) items.push(item('attention', 'logline.long', `Logline is ${formatted(words)} words; one sentence of 25 to 40 reads best.`, id));
        break;
    }
  }
  if (pages !== null) {
    items.push(pages <= 10
      ? item('ready', 'sample', `The play is ${formatted(pages)} ${pages === 1 ? 'page' : 'pages'}, so the sample is all of it`)
      : item('ready', 'sample', `Ten-page sample: the first ten of ${formatted(pages)} pages, ending with the last speech or direction that fits`));
  }
  if (musical) items.push(item('note', 'demos', "Theatres usually ask for demo recordings of the songs too, which you'll need to send yourself."));
  return { items, documents };
}

/** `PublishReadiness.radioPack`: the play's pack without its development history, plus the script's length. */
export function radioPack(source: CompileSource, pages: number | null): { items: ReadinessItem[]; documents: PackDocument[] } {
  const pack = playPack(source, null);
  const documents = pack.documents.filter((d) => d.kind !== 'development-history');
  const items = pack.items.filter((i) => i.id !== 'history.empty');
  if (pages !== null) items.push(item('ready', 'script', `The script: about ${plural(pages, 'page')}, roughly ${runningPhrase(pages)} on air`));
  return { items, documents };
}

/** `PublishReadiness.plural`: "3 pages", "1 song", for the middle of a sentence. */
const plural = (n: number, noun: string) => `${formatted(n)} ${noun}${n === 1 ? '' : 's'}`;

/** `RunningTime.minutes`: a page a minute; a multi-camera sitcom half that; a musical's book plus three and a half
 *  minutes a song. */
export function runningMinutes(pages: number, format: ScriptFormat, songs = 0): number {
  if (format === 'multicam') return swiftRound(pages / 2);
  return pages + swiftRound(songs * 3.5);
}

/** Swift's `.rounded()`: halves away from zero. */
const swiftRound = (x: number) => Math.sign(x) * Math.round(Math.abs(x));

/** `RunningTime.phrase`: "21 minutes", "an hour and 5 minutes". */
export function runningPhrase(minutes: number): string {
  if (minutes < 60) return `${minutes} ${minutes === 1 ? 'minute' : 'minutes'}`;
  const hours = Math.floor(minutes / 60), rest = minutes % 60;
  const h = hours === 1 ? 'an hour' : `${hours} hours`;
  return rest === 0 ? h : `${h} and ${rest} ${rest === 1 ? 'minute' : 'minutes'}`;
}

/** `RunningTime.radioSlots`: the usual lengths of a radio drama slot, in minutes. */
export const RADIO_SLOTS = [14, 28, 45, 60, 90];

/** `RunningTime.radioSlotNote`: a note when a radio play is far (more than five minutes, or a tenth of the slot) from
 *  every slot. */
export function radioSlotNote(minutes: number): string | null {
  if (minutes <= 0) return null;
  const nearest = RADIO_SLOTS.reduce((best, s) => (Math.abs(s - minutes) < Math.abs(best - minutes) ? s : best));
  if (Math.abs(nearest - minutes) <= Math.max(5, Math.floor(nearest / 10))) return null;
  const below = [...RADIO_SLOTS].reverse().find((s) => s < minutes), above = RADIO_SLOTS.find((s) => s > minutes);
  const place = below !== undefined && above !== undefined ? `between ${below} and ${above}`
    : below !== undefined ? `longer than ${below}` : above !== undefined ? `shorter than ${above}` : '';
  return `Radio drama slots usually run 14, 28, 45, 60 or 90 minutes; this runs about ${minutes}, ${place}.`;
}
